import type { TunnelRequestManager } from '@post-office/tunnel-request-manager/types';
import type { PipelineMessage } from '@post-office/shared-contracts/types';
import type { SerializableRecord } from '@post-office/serializable-record';

import type { AnyBackendMessageBody, RequestContext } from '../../../types';
import { type GenerateValidationFor } from '@atlassian/vindicated';
import { isVBackendMessageBodyArray } from './__generated__/validation/vBackendMessageBodyArray';

export type VBackendMessageBodyArray = GenerateValidationFor<{
	messages: SerializableRecord[];
}>;

/**
 * Parameters for executing a tunneled placement stage.
 */
export interface ExecuteTunneledStageParams {
	/** The request context from production */
	request: RequestContext;
	/** The pipeline messages to process */
	pipelineMessages: PipelineMessage[];
	/** The stage name (preInit, init, or postInit) */
	stageName: string;
	/** The placement ID */
	placementId: string;
	/** The message template IDs that should be tunneled */
	messageTemplateIds: string[];
	/** The remote tunnel manager for communicating with local machine */
	remoteTunnelManager: TunnelRequestManager;
	/** Function to strip non-serializable fields from messages */
	stripUnserializablePayload: (message: PipelineMessage) => AnyBackendMessageBody;
}

/**
 * Executes a placement stage on a staff member's local machine via atlastunnel.com.
 *
 * This function:
 * 1. Serializes the placement messages and request context
 * 2. Makes an HTTP request to the staff's local machine via RemoteTunnelRequestManager
 * 3. Merges the tunneled result with original pipeline messages to preserve non-serializable fields
 *
 * @warning
 * - Bypass buffer is not supported during tunnelling.
 * - If the placement stage uses the bypass buffer to pass metadata between stages (e.g., grouping data), this data will NOT
 * be tunneled to the local machine. This may cause different behavior between production and tunneled execution.
 *
 * @param params - Parameters for tunnel execution
 * @returns Modified pipeline messages from local execution
 * @throws Error if the tunnel request fails
 */
export const executePlacementStageTunneled = async ({
	request,
	pipelineMessages,
	stageName,
	placementId,
	messageTemplateIds,
	remoteTunnelManager,
	stripUnserializablePayload,
}: ExecuteTunneledStageParams): Promise<PipelineMessage[]> => {
	// Strip non-serializable fields from messages
	// Same behaviour as production
	const placementMessages = pipelineMessages.map(stripUnserializablePayload);

	const requestContext = {
		orchestrationContext: request.orchestrationContext,
		appConfig: request.appConfig,
		slauth: request.slauth,
		headers: request.headers,
		body: request.body,
		query: request.query,
		url: request.url,
	};

	const result = await remoteTunnelManager.makeRemoteTunnelRequest<VBackendMessageBodyArray>({
		route: '/in-app-v2/execute-placement-stage',
		method: 'POST',
		data: {
			placementId,
			stageName,
			messages: placementMessages,
			messageTemplateIds,
			requestContext,
		},
		validator: isVBackendMessageBodyArray,
	});

	// Merge with original pipeline messages to preserve non-serializable fields
	// Same behaviour as production
	const pipelineMessagesByMessageInstanceId: Record<string, PipelineMessage> = Object.fromEntries(
		pipelineMessages.map((pm) => [pm.messageInstanceId, pm]),
	);

	const combinedMessages: PipelineMessage[] = result.messages.map(
		(processedMessage) =>
			({
				...pipelineMessagesByMessageInstanceId[
					(processedMessage as unknown as PipelineMessage).messageInstanceId
				],
				...processedMessage,
			}) as PipelineMessage,
	);

	return combinedMessages;
};
